import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './InauguralPassSignup.css';

function InauguralPassSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
 
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [preferAlternativeReimbursement, setPreferAlternativeReimbursement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [journeyCode, setJourneyCode] = useState(null);
  const [isVerifyingSession, setIsVerifyingSession] = useState(true);
  
  // Validation states
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Verify session on mount
  useEffect(() => {
    // Check if session_id exists
    if (!sessionId) {
      alert('Invalid access. Please purchase a journey first.');
      navigate('/');
      return;
    }

    // Move verifySession inside useEffect
    const verifySession = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify-session/${sessionId}`);
        await response.json(); // We don't use the data, so no need to assign it
        
        if (!response.ok) {
          if (response.status === 409) {
            alert('This purchase has already been used. Please contact support if you believe this is an error.');
          } else {
            alert('Invalid or expired session. Please purchase a journey to continue.');
          }
          navigate('/');
        } else {
          // Session is valid, continue
          setIsVerifyingSession(false);
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        alert('Error verifying your purchase. Please try again.');
        navigate('/');
      }
    };

    // Call the function
    verifySession();
  }, [sessionId, navigate]);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validDomainRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    if (!validDomainRegex.test(email)) {
      return 'Please enter a valid email domain';
    }
    return '';
  };

  // Phone validation (optional field)
  const validatePhone = (phone) => {
    // Phone is optional, so empty is valid
    if (!phone) {
      return '';
    }
    // Remove all non-digit characters for length check
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length > 15) {
      return 'Phone number cannot exceed 15 digits';
    }
    if (!isValidPhoneNumber(phone)) {
      return 'Please enter a valid phone number';
    }
    return '';
  };

  // First name validation
  const validateFirstName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'First name is required';
    }
    if (name.trim().length < 2) {
      return 'First name must be at least 2 characters';
    }
    if (!/^[a-zA-Z-]+$/.test(name)) {
      return 'First name can only contain letters and hyphens';
    }
    if (name.length > 22) {
      return 'First name cannot exceed 22 characters';
    }
    return '';
  };

  // Password validation
  const validatePassword = (pass) => {
    if (!pass) {
      return 'Password is required';
    }
    if (pass.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setEmail(value);
    
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  // Handle first name change with validation
  const handleFirstNameChange = (e) => {
    let value = e.target.value;
    
    // Only allow letters and hyphens
    value = value.replace(/[^a-zA-Z-]/g, '');
    
    // Enforce max length
    if (value.length > 22) {
      value = value.slice(0, 22);
    }
    
    setFirstName(value);
    
    // Clear error when user starts typing
    if (firstNameError) {
      setFirstNameError('');
    }
  };

  // Handle phone change
  const handlePhoneChange = (value) => {
    // Check if the phone number has more than 15 digits
    if (value) {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 15) {
        return; // Don't update if it exceeds 15 digits
      }
    }
    
    setPhoneNumber(value || '');
    
    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError('');
    }
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError('');
    }
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    // Clear error when user starts typing
    if (confirmPasswordError) {
      setConfirmPasswordError('');
    }
  };

  // Check if passwords match
  const checkPasswordMatch = () => {
    if (!confirmPassword) return false;
    return password === confirmPassword;
  };

  // Validate all fields
  const validateForm = () => {
    const nameError = validateFirstName(firstName);
    const emailErr = validateEmail(email);
    const phoneErr = phoneNumber ? validatePhone(phoneNumber) : ''; // Only validate if provided
    const passErr = validatePassword(password);

    setFirstNameError(nameError);
    setEmailError(emailErr);
    setPhoneError(phoneErr);
    setPasswordError(passErr);

    return !nameError && !emailErr && !phoneErr && !passErr;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setShowConfirmation(true);
      setConfirmPassword(''); // Reset confirm password when showing popup
      setConfirmPasswordError('');
    }
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    setIsSubmitting(true);
    setShowVerificationScreen(true);

    try {
      const betaSignupData = {
        firstName: firstName.trim(),
        email: email.toLowerCase().trim(),
        phoneNumber: phoneNumber || undefined,  // Send undefined instead of empty string
        password: password,
        venmoUsername: venmoUsername.trim() || undefined,
        preferAlternativeReimbursement: preferAlternativeReimbursement,
        sessionId: sessionId || null,
        timestamp: new Date().toISOString()
      };
      
      // Log what we're sending for debugging
      console.log('Sending to backend:', betaSignupData);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inaugural-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(betaSignupData)
      });

      const data = await response.json();
      
      // Log the error for debugging
      if (!response.ok) {
        console.error('Backend error response:', data);
        console.error('Status:', response.status);
        if (data.errors) {
          console.error('Validation errors:', data.errors);
        }
      }

      if (!response.ok) {
        // Handle specific errors
        if (response.status === 409) {
          if (data.field === 'email') {
            alert('This email is already registered. Please use a different email or sign in.');
          } else if (data.field === 'phone') {
            alert('This phone number is already registered. Please use a different phone number.');
          }
        } else if (response.status === 400) {
          if (data.field === 'password') {
            alert('Password is too weak. Please use at least 6 characters.');
          } else if (data.field === 'phone') {
            alert('Invalid phone number format. Please enter a valid phone number.');
          } else {
            alert(data.error || 'Invalid input. Please check your information.');
          }
        } else {
          alert(data.error || 'Error creating account. Please try again.');
        }
        setShowVerificationScreen(false);
        setIsSubmitting(false);
        return;
      }

      // Success! Get journey details
      let journeyCode = data.journeyCode;
      let fetchedRouteName = 'Your Adventure'; // default fallback
      let startDate = null;
      let endDate = null;
      
      if (journeyCode) {
        setJourneyCode(journeyCode);
        
        // Fetch journey data to get route name and dates BEFORE navigating
        // Use retry logic to handle race condition between webhook and frontend
        try {
          let journeyData = null;
          let retryCount = 0;
          const maxRetries = 5;
          const retryDelay = 2000; // 2 seconds
          
          while (retryCount < maxRetries && !journeyData) {
            try {
              const journeyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/journeyByCode/${journeyCode}`);
              if (journeyResponse.ok) {
                journeyData = await journeyResponse.json();
                fetchedRouteName = journeyData.routeName || 'Your Adventure';
                startDate = journeyData.startDate;
                endDate = journeyData.endDate;
                console.log('Fetched journey data:', { routeName: fetchedRouteName, startDate, endDate });
                break;
              } else if (journeyResponse.status === 404) {
                // Journey not found yet - webhook still processing
                console.log(`Journey not found yet, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
                retryCount++;
                if (retryCount < maxRetries) {
                  await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
              } else {
                console.error('Unexpected response status:', journeyResponse.status);
                break;
              }
            } catch (fetchError) {
              console.error('Error fetching journey data:', fetchError);
              retryCount++;
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
          }
          
          if (!journeyData) {
            console.warn('Could not fetch journey data after all retries, using defaults');
          }
        } catch (error) {
          console.error('Error in journey data retry logic:', error);
        }
        
        await new Promise(resolve => setTimeout(resolve, 5000)); // Show verification screen longer
      } else {
        await new Promise(resolve => setTimeout(resolve, 4000));
      }
      
      // Navigate to confirmation page with all the data
      navigate('/inaugural-confirmation', { 
        state: { 
          firstName: firstName,
          journeyCode: journeyCode,
          routeName: fetchedRouteName,
          email: email,
          startDate: startDate,
          endDate: endDate
        } 
      });
     
    } catch (error) {
      console.error('Error submitting beta signup:', error);
      alert('Network error. Please check your connection and try again.');
      setShowVerificationScreen(false);
      setIsSubmitting(false);
    }
  };

  // Format phone number for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    // PhoneInput already formats the number nicely
    return phone;
  };

  // Show loading while verifying session
  if (isVerifyingSession) {
    return (
      <div className="inaugural-signup-page">
        <div className="verification-screen">
          <div className="verification-content">
            <div className="verification-spinner"></div>
            <h2>Verifying Your Purchase</h2>
            <p className="verification-note">
              Please wait while we verify your session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showVerificationScreen) {
    return (
      <div className="inaugural-signup-page">
        <div className="verification-screen">
          <div className="verification-content">
            <div className="verification-spinner"></div>
            <h2>Verifying Your Information</h2>
            <div className="verification-checklist">
              <div className="checklist-item">
                <span className="check-icon">✓</span>
                <span>Saving contact details to database...</span>
              </div>
              <div className="checklist-item">
                <span className="check-icon">✓</span>
                <span>Sending confirmation email to {email}...</span>
              </div>
              <div className="checklist-item">
                <span className="check-icon">✓</span>
                <span>Phone number saved for future communications...</span>
              </div>
            </div>
            {journeyCode && (
              <div style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '8px',
                border: '2px solid #FFD700',
                textAlign: 'center'
              }}>
                <p style={{ color: '#FFD700', fontSize: '18px', marginBottom: '10px' }}>
                  Your Journey Code:
                </p>
                <p style={{ color: '#FFD700', fontSize: '36px', fontWeight: 'bold', letterSpacing: '5px' }}>
                  {journeyCode}
                </p>
                <p style={{ color: '#FFFFFF', fontSize: '14px', marginTop: '10px' }}>
                  Please save this code!
                </p>
              </div>
            )}
            <p className="verification-note">
              Please check your email for confirmation message.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inaugural-signup-page">
      {/* Logo outside and above container */}
      <div className="inaugural-logo-wrapper">
        <img src="/images/logodownsized.webp" alt="BackPass Logo" className="inaugural-logo" />
      </div>

      <div className="inaugural-signup-container">
        <h1>Welcome to BackPass Inaugural Pass</h1>
        <p className="inaugural-description">
          Provide your information so we can reach out quickly! Don't navigate away from this page. Any questions matthew@backpass.co
        </p>

        <form onSubmit={handleSubmit} className="inaugural-signup-form" noValidate>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={handleFirstNameChange}
              onBlur={() => setFirstNameError(validateFirstName(firstName))}
              required
              placeholder="Enter your first name"
              className={firstNameError ? 'error' : ''}
              maxLength={22}
            />
            {firstNameError && <span className="error-message">{firstNameError}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => setEmailError(validateEmail(email))}
              required
              placeholder="Your@Email.com"
              autoComplete="email"
              className={emailError ? 'error' : ''}
            />
            {emailError && <span className="error-message">{emailError}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number (Optional)</label>
            <PhoneInput
              id="phoneNumber"
              defaultCountry="US"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onBlur={() => setPhoneError(validatePhone(phoneNumber))}
              placeholder="Enter phone number"
              className={`phone-input-wrapper ${phoneError ? 'error' : ''}`}
              autoComplete="tel"
              numberInputProps={{
                className: 'phone-number-input'
              }}
            />
            {phoneError && <span className="error-message">{phoneError}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => setPasswordError(validatePassword(password))}
              required
              placeholder="Enter your password"
              autoComplete="new-password"
              className={passwordError ? 'error' : ''}
            />
            {passwordError && <span className="error-message">{passwordError}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="venmoUsername">Venmo Username (Optional)</label>
            <input
              type="text"
              id="venmoUsername"
              value={venmoUsername}
              onChange={(e) => setVenmoUsername(e.target.value)}
              placeholder="@your-venmo-username"
              className="venmo-input"
            />
            <small className="form-help-text">For potential reimbursements or refunds</small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={preferAlternativeReimbursement}
                onChange={(e) => setPreferAlternativeReimbursement(e.target.checked)}
              />
              <span>I prefer an alternative reimbursement method if needed</span>
            </label>
          </div>

          <button
            type="submit"
            className="inaugural-submit-button"
            disabled={isSubmitting}
          >
            Confirm Contact Details
          </button>
        </form>
      </div>

      {/* Confirmation Popup */}
      {showConfirmation && (
        <div className="confirmation-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="confirmation-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="popup-close"
              onClick={() => setShowConfirmation(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Important!</h3>
            <p>
              This contact information is <strong>critical</strong> - it's how we'll send you your Inaugural Pass instructions and provide personalized support throughout your journey.
            </p>
            <p>Please confirm your contact information:</p>
            <div className="confirmation-details">
              <p><strong>Name:</strong> {firstName}</p>
              <p><strong>Email:</strong> {email}</p>
              {phoneNumber && <p><strong>Phone:</strong> {formatPhoneDisplay(phoneNumber)}</p>}
            </div>
            
            {/* Password confirmation section */}
            <div className="password-confirmation">
              <label htmlFor="confirmPassword">Re-enter Password Inside of Box</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                placeholder="I'm Box!"
                className="confirm-password-input"
              />
              {confirmPassword && !checkPasswordMatch() && (
                <span className="password-mismatch">Password doesn't match</span>
              )}
            </div>

            {checkPasswordMatch() && (
              <div className="confirmation-buttons">
                <button 
                  className="confirm-yes"
                  onClick={handleConfirm}
                >
                  Yes, Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InauguralPassSignup;